import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import Chart from 'chart.js/auto';
import React from 'react';

import 'chartjs-adapter-luxon';
import useRect from 'hooks/common/useRect';
import useSetChartData from 'hooks/app/useSetChartData';

const dataLabels = [
  '월',
  '화',
  '수',
  '목',
  '금',
  '토',
  '일',
  '주중',
  '주말'
]

function getRect(element, borderWidth) {
  const half = element.width / 2;
  return {
    x: element.x - half + borderWidth / 2,
    y: Math.min(element.y, element.base) + borderWidth / 2,
    width: element.width - borderWidth,
    height: Math.abs(element.base - element.y) - borderWidth,
  };
}

function getRadii(element) {
  const { enableBorderRadius, borderSkipped, options } = element;
  const { borderRadius } = options;
  if (!enableBorderRadius) return 0;
  return [
    borderSkipped.top ? 0 : borderRadius,
    borderSkipped.top ? 0 : borderRadius,
    borderSkipped.bottom ? 0 : borderRadius,
    borderSkipped.bottom ? 0 : borderRadius,
  ];
}

const config = (hash, backgroundColor, middleColor, onComplete, duration) => ({
  type: 'bar',
  data: {
    labels: dataLabels,
    datasets: [
      {
        data: hash[0][0],
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor,
        barPercentage: 1,
        stack: '0',
      }, {
        data: hash[0][1].map(value => Math.abs(value)),
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor: hash[0][1].map(value => value < 0 ? '#ffffff' : middleColor),
        barPercentage: 1,
        stack: '0',
      }, {
        data: hash[0][2],
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor: '#fcd9df',
        barPercentage: 1,
        stack: '0',
      },
      {
        data: hash[1][0],
        borderColor: '#33333380',
        borderWidth: 0.5,
        dashedBorderDash: [4, 4],
        dashedBorderWidth: 0.5,
        dashedBorderSide: { left: true, right: true },
        backgroundColor,
        barPercentage: 1,
        stack: '1',
      }, {
        data: hash[1][1].map(value => Math.abs(value)),
        borderColor: '#33333380',
        borderWidth: 0.5,
        dashedBorderDash: [4, 4],
        dashedBorderWidth: 0.5,
        dashedBorderSide: { left: true, right: true },
        backgroundColor: hash[1][1].map(value => value < 0 ? 'transparent' : middleColor),
        barPercentage: 1,
        stack: '1',
      }, {
        data: hash[1][2],
        borderColor: '#33333380',
        borderWidth: 0.5,
        dashedBorderDash: [4, 4],
        dashedBorderWidth: 0.5,
        dashedBorderSide: { left: true, right: true },
        backgroundColor: '#fcd9df',
        barPercentage: 1,
        stack: '1',
      },
      {
        data: hash[2][0],
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor,
        barPercentage: 1,
        stack: '2',
      }, {
        data: hash[2][1].map(value => Math.abs(value)),
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor: hash[2][1].map(value => value < 0 ? '#ffffff' : middleColor),
        barPercentage: 1,
        stack: '2',
      }, {
        data: hash[2][2],
        borderColor: '#33333380',
        borderWidth: 0.5,
        backgroundColor: '#fcd9df',
        barPercentage: 1,
        stack: '2',
      },
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        left: 110,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        border: {
          width: 0,
        },
        position: 'top',
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        max: 100,
        display: false,
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },

    },
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const context = chart.getContext('2d', { willReadFrequently: !duration });
      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;
      const { dashedBorderDash, dashedBorderWidth, dashedBorderSide } = dataset;
      if (!dashedBorderDash?.length || !dashedBorderWidth) return;
      meta.data.forEach((element, dataIndex) => {
        const fullContext = {
          ...context,
          dataset,
          dataIndex,
        };
        const borderWidth =
          dashedBorderWidth instanceof Function
            ? dashedBorderWidth(fullContext, element)
            : dashedBorderWidth;
        if (borderWidth === 0) return;

        const ctx = chart.ctx;
        const { x, y, width, height } = getRect(element, borderWidth);
        const radii = getRadii(element);
        ctx.beginPath();
        ctx.lineWidth = borderWidth;
        ctx.strokeStyle = element.options.borderColor;
        ctx.setLineDash(dashedBorderDash);
        if (!dashedBorderSide) {
          ctx.roundRect(x, y, width, height, radii);
        } else {
          if (dashedBorderSide.left) {
            ctx.moveTo(x, y);
            ctx.lineTo(x, y + height);
          }
          if (dashedBorderSide.right) {
            ctx.moveTo(x + width, y);
            ctx.lineTo(x + width, y + height);
          }
        }
        ctx.stroke();
        ctx.save();
      });
    }
  }]
});

const array = Array.from({ length: 9 }).map((_) => 0);

export default function ExerciseAnalysisChart(props) {
  const {
    viewMode,
    stepData = {},
    caloryData = {},
    duration,
    ...rest
  } = props;

  const setChartData1 = useSetChartData('StepAnalysisWeekChart');
  const setChartData2 = useSetChartData('CaloryAnalysisWeekChart');
  const setChartData = React.useMemo(() => {
    if (viewMode === 'ReportExerciseSteps') {
      return setChartData1;
    } else {
      return setChartData2;
    }
  }, [setChartData1, setChartData2, viewMode]);


  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (viewMode === 'ReportExerciseSteps') {
      if (stepData.uninitialized) return;
    } else if (viewMode === 'ReportExerciseCalories') {
      if (caloryData.uninitialized) return;
    }
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [caloryData.uninitialized, duration, setChartData, stepData.uninitialized, viewMode]);

  const hash = React.useMemo(() => {
    const data = {
      0: { 0: [...array], 1: [...array], 2: [...array] },
      1: { 0: [...array], 1: [...array], 2: [...array] },
      2: { 0: [...array], 1: [...array], 2: [...array] },
    };

    if (viewMode === 'ReportExerciseSteps') {
      const {
        active_analyze_day_bldsugr_graph = [],
        active_analyze_day_step_graph = [],
      } = stepData;

      active_analyze_day_step_graph.forEach((item, index) => {
        const {
          avg_step_rate_morn,
          avg_step_rate_lun,
          avg_step_rate_din,
        } = item;
        data[0][0][index] = avg_step_rate_morn;
        data[1][0][index] = avg_step_rate_lun;
        data[2][0][index] = avg_step_rate_din;
      });

      active_analyze_day_bldsugr_graph.forEach((item, index) => {
        const {
          avg_bldsugr_rate_morn,
          avg_bldsugr_rate_lun,
          avg_bldsugr_rate_din,
        } = item;
        data[0][2][index] = avg_bldsugr_rate_morn;
        data[1][2][index] = avg_bldsugr_rate_lun;
        data[2][2][index] = avg_bldsugr_rate_din;
      });


    } else if (viewMode === 'ReportExerciseCalories') {
      const {
        active_analyze_day_bldsugr_graph = [],
        active_analyze_day_cal_list = [],
      } = caloryData;

      active_analyze_day_cal_list.forEach((item, index) => {
        const {
          avg_cal_rate_morn,
          avg_cal_rate_lun,
          avg_cal_rate_din,
        } = item;
        data[0][0][index] = avg_cal_rate_morn;
        data[1][0][index] = avg_cal_rate_lun;
        data[2][0][index] = avg_cal_rate_din;
      });

      active_analyze_day_bldsugr_graph.forEach((item, index) => {
        const {
          avg_bldsugr_rate_morn,
          avg_bldsugr_rate_lun,
          avg_bldsugr_rate_din,
        } = item;
        data[0][2][index] = avg_bldsugr_rate_morn;
        data[1][2][index] = avg_bldsugr_rate_lun;
        data[2][2][index] = avg_bldsugr_rate_din;
      });
    }

    for (let i = 0; i < 3; ++i) {
      for (let j = 0; j < 9; ++j) {
        const sum = data[i][0][j] + data[i][2][j];
        if (100 < sum) {
          const diff = sum - 100;
          data[i][0][j] -= diff;
          data[i][1][j] = diff;
          data[i][2][j] = 100 - data[i][0][j] - data[i][1][j];
        } else if (sum < 100) {
          data[i][1][j] = -(100 - data[i][0][j] - data[i][2][j]);
        }
      }
    }

    return data;
  }, [caloryData, stepData, viewMode]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const color = viewMode === 'ReportExerciseSteps' ? '#d8e4fe' : '#d3f4e3';
    const middle = viewMode === 'ReportExerciseSteps' ? '#dcc3de' : '#d8d0c9';
    const chart = new Chart(canvasRef.current, config(hash, color, middle, onComplete, duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const color = viewMode === 'ReportExerciseSteps' ? '#d8e4fe' : '#d3f4e3';
    const middle = viewMode === 'ReportExerciseSteps' ? '#dcc3de' : '#d8d0c9';
    const configuration = config(hash, color, middle, onComplete, duration);
    configuration.data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((datum, datumIndex) => {
        chartRef.current.config.data.datasets[datasetIndex].data[datumIndex] = datum;
      });
      chartRef.current.config.data.datasets[datasetIndex].backgroundColor = dataset.backgroundColor;
    });
    chartRef.current.update();
  }, [hash, onComplete, viewMode, duration]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div {...ex('ExerciseAnalysisChart', rest)}>
      <div {...ex('Chart', { ref })}>
        <canvas
          {...{
            ref: canvasRef,
            width: rect.width,
            height: rect.height,
          }}
        />
      </div>
    </div>
  );
}
